import {
  Button,
  CircularProgress,
  Grid,
  // LinearProgress,
  Theme,
  withStyles,
  Typography
} from '@material-ui/core'
import { Fragment } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { reduxForm } from 'redux-form'
import { MediaGallery } from '../../../components/Display'
import ErrorMessage from '../../../components/Error/ErrorMessage'
import FileInput from '../../../components/FormInput/FileInput'
import { addStepCompletion } from '../../../config/googleTagManager'
import { BucketObject } from '../../../services/storage/types'
import { MuiClasses } from '../../../types'
import { ApartmentMedia } from '../../../types/apartments'
import { ReduxForm } from '../../../types/forms'

import {
  ListingCreationSteps,
  StepCompletionEventNames
} from '../../../types/GTM'
import Suggest from '../Stepper/Steps/Suggest'
import { apartmentStyles } from '../../../styles'
const styles = (theme: Theme) => ({
  ...apartmentStyles(theme),
  photoupload: {
    marginTop: theme.spacing(2)
  },
  removeIcon: {
    zIndex: '99999' as any,
    position: 'absolute' as any,
    top: '0px',
    right: '10px',
    cursor: 'pointer',
    paddingTop: 8
  },
  imageTile: {
    height: '100%'
  },
  primaryBtn: {
    backgroundColor: '#4f9b44',
    marginTop: '25px',
    '&:hover': {
      backgroundColor: '#3f7c36'
    }
  },
  skipBtn: {
    minWidth: '245px',
    marginTop: '8px',
    padding: '10px 60px',
    borderRadius: '10px',
    height: '42px',
    boxShadow: '0px 0px 15px rgba(0, 0, 0, 0.15)',
    textTransform: 'none' as const
  },
  infoBoxWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    '@media (max-width:1279px)': {
      justifyContent: 'center',
      '& > *': { maxWidth: '550px' }
    }
  },
  mediaUploadRow: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '20px'
  },
  dropzoneColumn: {
    flex: '0 1 75%',
    '@media (max-width:1279px)': {
      flex: '1 1 100%'
    }
  },
  suggestColumn: {
    flex: '1',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-end',
    '@media (max-width:1279px)': {
      flex: '1 1 100%',
      justifyContent: 'center',
      marginTop: theme.spacing(2)
    }
  }
})

export interface FormFile {
  path: string
}

interface ApartmentMediaFormProps extends WithTranslation {
  isStep?: boolean
  classes: MuiClasses
  submitting: boolean
  handleSubmit: (arg: any) => any
  addApartmentMedia: (arg: any) => any
  deleteMedia: (arg: any) => any
  reorderMedia: (arg: any) => any
  confirmMedia?: () => void
  media: ApartmentMedia
  files: BucketObject[]
  beforeLogin?: true
  isMobile: any
  currentStep: any
  doStepForward: any
  doStepBack: () => void
  signUpBeforeLogin: () => void
}

const supportedMediaTypes =
  'image/jpeg, image/png, image/gif, image/webp, image/tiff, image/svg'

const ApartmentMediaForm = ({
  t,
  classes,
  submitting,
  handleSubmit,
  addApartmentMedia,
  deleteMedia,
  reorderMedia,
  files,
  confirmMedia,
  media,
  isStep,
  beforeLogin,
  error,
  isMobile,
  currentStep,
  doStepForward,
  doStepBack,
  signUpBeforeLogin
}: ApartmentMediaFormProps & ReduxForm) => {
  return (
    <form onSubmit={handleSubmit(addApartmentMedia)}>
      <Grid container>
        <Grid container className={classes.mediaUploadRow}>
          <Grid className={`${classes.largeForm} ${classes.dropzoneColumn}`} item>
            <Grid container>
              <Grid item xs={12} sm={12}>
                <FileInput
                  name='files'
                  label={t('apartment.mediaUpload.addMedia')}
                  dropzoneOptions={{
                    accept: supportedMediaTypes,
                    /** max size in Bytes **/
                    maxSize: 99 * 1024 * 1024
                  }}
                  beforeLogin={beforeLogin}
                  submitForm={handleSubmit(addApartmentMedia)}
                  submittingMedia={submitting}
                />
              </Grid>
            </Grid>
          </Grid>{' '}
          <Grid className={classes.suggestColumn}>
            <div className={classes.infoBoxWrapper}>
              <Suggest
                fullWidth
                content={t('apartment.hints.step11Hint')}
                padding='0'
                isModal
              />
            </div>
          </Grid>
        </Grid>
        <Grid item sm={12} style={{ width: '100%' }}>
          {files && files.length > 0 && (
            <Fragment>
              {/** margin of 1px is needed to fix shaky dialog on small screen */}
              <Grid item xs={12} style={{ margin: '1px' }}>
                <MediaGallery
                  t={t}
                  media={media}
                  files={files}
                  withDelete={{ deleteMedia }}
                  reorderMedia={reorderMedia}
                />
              </Grid>
            </Fragment>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          className={classes.tabletMaxWidth}
          style={{ maxWidth: '550px' }}
        >
          {' '}
          <ErrorMessage error={error} />
          {error === t('error.beforeLoginApartment.NotAuthorizedException') && (
            <RouterLink
              to='/forgotpassword/request'
              rel='noopener noreferrer'
              target='_blank'
            >
              <Typography
                className={classes.signup}
                variant='body2'
                gutterBottom
              >
                {t('login.forgotPassword')}
              </Typography>
            </RouterLink>
          )}
          {error && (
            <Grid
              item
              sm={12}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <Button className={classes.skipBtn} onClick={doStepBack}>
                {t('general.back')}
              </Button>
            </Grid>
          )}
          {confirmMedia && isStep && files && files.length > 0 && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant='contained'
                color='primary'
                className={classes.primaryBtn}
                style={{
                  height: '42px'
                }}
                onClick={handleSubmit(confirmMedia)}
              >
                {submitting && beforeLogin ? (
                  <Grid container justify='center'>
                    <CircularProgress
                      size={25}
                      style={{
                        color: '#fff',
                        marginLeft: '5px'
                      }}
                    />
                  </Grid>
                ) : (
                  <> {t('apartment.mediaUpload.confirmMedia')}</>
                )}
              </Button>
            </Grid>
          )}
          {currentStep === 11 && (!files || files.length < 1) && (
            <Grid
              item
              sm={12}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              {!beforeLogin ? (
                <Button
                  onClick={() => {
                    addStepCompletion(
                      StepCompletionEventNames.listingCreation,
                      ListingCreationSteps.proceededFromPhotos
                    )
                    doStepForward()
                  }}
                  className={classes.skipBtn}
                >
                  <Typography>
                    {submitting ? (
                      <Grid container>
                        <CircularProgress
                          size={25}
                          style={{ color: '#4f9b44', marginLeft: '5px' }}
                        />
                      </Grid>
                    ) : (
                      <> {t('apartment.mediaUpload.addLater')}</>
                    )}
                  </Typography>
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit(signUpBeforeLogin)}
                  className={classes.skipBtn}
                >
                  <Typography>
                    {' '}
                    {submitting ? (
                      <Grid container>
                        <CircularProgress
                          size={25}
                          style={{ color: '#4f9b44', marginLeft: '5px' }}
                        />
                      </Grid>
                    ) : (
                      <> {t('apartment.mediaUpload.addLater')}</>
                    )}
                  </Typography>
                </Button>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
    </form>
  )
}

export default reduxForm({
  form: 'ApartmentMediaForm'
})(
  withStyles(styles)(withTranslation()(ApartmentMediaForm as any)) as any
) as any
